import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, call } from '../api';
import { useApp } from '../context';
import { addToQueue, fmtTime, fromPlexTrack, hydrate, playNext, playQueue, setMusicPlex, storable, useMusic, type MusicTrack } from '../musicPlayer';
import type { PlexClient, PlexItem } from '../plex';
import { linkProps, navigate } from '../router';
import { useResolveAll, viaLabel } from '../musicResolve';

/** Muziek: Plex-muziek en muziek-addons samen, met links een menu en eigen playlists. */

type SourceFilter = 'all' | 'plex' | 'addons';

interface MusicAddon { id: string; name: string; icon: string | null; resources: string[]; catalogs: { key: string; name: string; type: string }[] }
interface ATrack extends MusicTrack { addonId: string; addonName: string }
interface AAlbum { addonId: string; addonName: string; id: string; title: string; artist: string; artwork: string | null; trackCount: number | null; year: string | null }
interface AArtist { addonId: string; addonName: string; id: string; name: string; artwork: string | null; genres: string[] }
interface APlaylist { addonId: string; addonName: string; id: string; title: string; artwork: string | null; creator: string | null; trackCount: number | null }
interface OwnPlaylist { id: string; name: string; count: number; artwork: string | null; updatedAt: number }
interface Saved { kind: 'album' | 'artist' | 'playlist'; src: 'plex' | 'addon' | 'deezer'; id: string; addonId?: string; title: string; sub?: string | null; artwork?: string | null }
interface Library { favorites: MusicTrack[]; playlists: OwnPlaylist[]; recent: MusicTrack[]; saved: Saved[] }

interface Container<T> { MediaContainer: T & { size?: number } }

const FILTER_KEY = 'tumedia.music.source';

const errText = (err: unknown, fallback: string) => (err instanceof ApiError && err.message !== err.code ? err.message : fallback);

// ---------- Gedeelde data ----------

let libPromise: Promise<Library> | null = null;
const libListeners = new Set<() => void>();
function loadLibrary(force = false): Promise<Library> {
  if (!libPromise || force) libPromise = call<Library>('/api/music/library').then((l) => ({ ...l, saved: l.saved ?? [] })).catch(() => ({ favorites: [], playlists: [], recent: [], saved: [] }));
  return libPromise;
}
function libraryChanged() {
  libPromise = null;
  libListeners.forEach((l) => l());
}
function useLibrary(): Library | null {
  const [lib, setLib] = useState<Library | null>(null);
  useEffect(() => {
    const l = () => void loadLibrary().then(setLib);
    l();
    libListeners.add(l);
    return () => {
      libListeners.delete(l);
    };
  }, []);
  return lib;
}

function plexAlbumCard(plex: PlexClient, a: PlexItem): Card {
  const sub = [a.parentTitle, a.year].filter(Boolean).join(' · ');
  return { key: `p:${a.ratingKey}`, title: a.title, sub, art: plex.image(a.thumb, 300, 300), href: `/music/album?src=plex&id=${a.ratingKey}`, source: 'Plex', save: { kind: 'album', src: 'plex', id: a.ratingKey, title: a.title, sub } };
}
function plexArtistCard(plex: PlexClient, a: PlexItem): Card {
  return { key: `pa:${a.ratingKey}`, title: a.title, sub: 'Artiest', art: plex.image(a.thumb, 300, 300), href: `/music/artist?src=plex&id=${a.ratingKey}`, round: true, source: 'Plex', save: { kind: 'artist', src: 'plex', id: a.ratingKey, title: a.title } };
}
const addonAlbumCard = (a: AAlbum): Card => ({ key: `a:${a.addonId}:${a.id}`, title: a.title, sub: [a.artist, a.year].filter(Boolean).join(' · '), art: a.artwork ?? undefined, href: `/music/album?src=addon&addon=${a.addonId}&id=${encodeURIComponent(a.id)}`, source: a.addonName, save: { kind: 'album', src: 'addon', addonId: a.addonId, id: a.id, title: a.title, sub: [a.artist, a.year].filter(Boolean).join(' · '), artwork: a.artwork } });
const addonArtistCard = (a: AArtist): Card => ({ key: `aa:${a.addonId}:${a.id}`, title: a.name, sub: a.genres.slice(0, 2).join(', ') || 'Artiest', art: a.artwork ?? undefined, href: `/music/artist?src=addon&addon=${a.addonId}&id=${encodeURIComponent(a.id)}`, round: true, source: a.addonName, save: { kind: 'artist', src: 'addon', addonId: a.addonId, id: a.id, title: a.name, artwork: a.artwork } });
const addonPlaylistCard = (p: APlaylist): Card => ({ key: `ap:${p.addonId}:${p.id}`, title: p.title, sub: p.creator ?? (p.trackCount ? `${p.trackCount} nummers` : 'Playlist'), art: p.artwork ?? undefined, href: `/music/addonplaylist?addon=${p.addonId}&id=${encodeURIComponent(p.id)}`, source: p.addonName, save: { kind: 'playlist', src: 'addon', addonId: p.addonId, id: p.id, title: p.title, sub: p.creator, artwork: p.artwork } });

type Card = { key: string; title: string; sub: string; art?: string; href: string; round?: boolean; source?: string; save?: Saved };

/** Link naar de pagina van een bewaard item. */
function savedHref(x: Saved): string {
  if (x.kind === 'playlist') return x.src === 'deezer' ? `/music/dplaylist?id=${encodeURIComponent(x.id)}` : `/music/addonplaylist?addon=${x.addonId}&id=${encodeURIComponent(x.id)}`;
  return `/music/${x.kind}?src=${x.src}${x.addonId ? `&addon=${x.addonId}` : ''}&id=${encodeURIComponent(x.id)}`;
}
function savedCard(plex: PlexClient, x: Saved): Card {
  const art = x.src === 'plex' ? plex.image(`/library/metadata/${x.id}/thumb`, 300, 300) : x.artwork ?? undefined;
  return { key: `s:${x.kind}:${x.src}:${x.addonId ?? ''}:${x.id}`, title: x.title, sub: x.sub ?? (x.kind === 'artist' ? 'Artiest' : x.kind === 'playlist' ? 'Playlist · gevolgd' : 'Album'), art, href: savedHref(x), round: x.kind === 'artist', source: x.src === 'plex' ? 'Plex' : x.src === 'deezer' ? 'Deezer' : undefined, save: x };
}
const sameSaved = (a: Saved, b: Saved) => a.kind === b.kind && a.src === b.src && a.id === b.id && (a.addonId ?? '') === (b.addonId ?? '');

/** ♡ in Mijn muziek (albums, artiesten; een playlist bewaren = volgen). */
function SaveButton({ item, big }: { item: Saved; big?: boolean }) {
  const lib = useLibrary();
  const on = !!lib?.saved.some((x) => sameSaved(x, item));
  const label = on ? (item.kind === 'playlist' ? 'Volgend' : 'In Mijn muziek') : item.kind === 'playlist' ? 'Volgen' : 'Bewaren';
  const toggle = async (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    await call('/api/music/saved', { method: 'PUT', body: JSON.stringify({ item, on: !on }) }).catch(() => undefined);
    libraryChanged();
  };
  if (big) return <button className={`btn${on ? ' is-on' : ''}`} aria-pressed={on} onClick={toggle}>{on ? '♥' : '♡'} {label}</button>;
  return <button className={`music-save${on ? ' is-on' : ''}`} aria-pressed={on} aria-label={`${item.title}: ${label}`} title={label} onClick={toggle}>{on ? '♥' : '♡'}</button>;
}

/** Alle nummers (album, playlist, populair) aan een eigen playlist toevoegen, of een nieuwe maken. */
function AddAllButton({ tracks, name }: { tracks: MusicTrack[]; name: string }) {
  const lib = useLibrary();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  if (!tracks.length) return null;
  const done = (m: string) => { setMsg(m); window.setTimeout(() => { setMsg(null); setOpen(false); }, 1200); };
  return (
    <span className="music-addall">
      <button className="btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>＋ Aan playlist</button>
      {open && (
        <div className="track-menu" role="menu">
          <button role="menuitem" onClick={async () => {
            const r = await call<{ playlist: { id: string } }>('/api/music/playlists', { method: 'POST', body: JSON.stringify({ name, tracks: tracks.map(storable) }) }).catch(() => null);
            libraryChanged();
            if (r) navigate(`/music/playlist?id=${r.playlist.id}`);
          }}>Nieuwe playlist “{name}”</button>
          {lib?.playlists.map((p) => (
            <button key={p.id} role="menuitem" onClick={async () => { await call(`/api/music/playlists/${p.id}`, { method: 'PATCH', body: JSON.stringify({ add: tracks.map(storable) }) }).catch(() => undefined); libraryChanged(); done(`${tracks.length} nummers aan ${p.name} toegevoegd`); }}>＋ {p.name}</button>
          ))}
          {msg && <span className="subtle" role="status">{msg}</span>}
        </div>
      )}
    </span>
  );
}

// ---------- Scherm ----------

export function Music({ section, params }: { section: string; params: URLSearchParams }) {
  const { plex, libraries, me } = useApp();
  const lib = useLibrary();
  const [addons, setAddons] = useState<MusicAddon[]>([]);
  const [filter, setFilterState] = useState<SourceFilter>(() => {
    try {
      return (localStorage.getItem(FILTER_KEY) as SourceFilter) || 'all';
    } catch {
      return 'all';
    }
  });
  const setFilter = (f: SourceFilter) => {
    setFilterState(f);
    try {
      localStorage.setItem(FILTER_KEY, f);
    } catch {
      // geen opslag
    }
  };
  const musicLibs = libraries.filter((l) => l.type === 'artist');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setMusicPlex(plex), [plex]);
  useEffect(() => {
    call<{ addons: MusicAddon[] }>('/api/music/sources').then((r) => setAddons(r.addons)).catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 403) setError(e.message !== e.code ? e.message : 'Muziek staat uit voor dit profiel.');
    });
  }, []);

  const showPlex = filter !== 'addons' && musicLibs.length > 0;
  const showAddons = filter !== 'plex' && addons.length > 0;

  const nav: [string, string, string][] = [
    ['home', 'Home', '/music'],
    ['discover', 'Ontdekken', '/music/discover'],
    ['chart', 'Hitlijsten', '/music/chart'],
    ['search', 'Zoeken', '/music/search'],
    ...(musicLibs.length ? ([['albums', 'Albums · Plex', '/music/albums'], ['artists', 'Artiesten · Plex', '/music/artists']] as [string, string, string][]) : []),
    ['favorites', 'Mijn muziek', '/music/favorites'],
  ];

  if (error) return <main className="page"><p className="placeholder-row error" role="alert">{error}</p></main>;

  return (
    <main className="music-page">
      <aside className="music-side" aria-label="Muziekmenu" data-focus-group>
        <div className="music-side-title">Muziek</div>
        {nav.map(([id, label, href]) => (
          <a key={id} className="music-side-item" {...linkProps(href)} aria-current={section === id ? 'page' : undefined}>{label}</a>
        ))}
        {musicLibs.length > 0 && <div className="music-side-sub">Plex: {musicLibs.map((l) => l.title).join(', ')}</div>}
        <div className="music-side-head">
          <span>Playlists</span>
          <NewPlaylistButton />
        </div>
        {lib?.playlists.map((p) => (
          <a key={p.id} className="music-side-item" {...linkProps(`/music/playlist?id=${p.id}`)} aria-current={section === 'playlist' && params.get('id') === p.id ? 'page' : undefined}>{p.name}<span className="subtle"> · {p.count}</span></a>
        ))}
        <PlexPlaylistsSide current={section === 'plexplaylist' ? params.get('id') : null} />
        {addons.length > 0 && (
          <>
            <div className="music-side-head"><span>Addons</span></div>
            {addons.map((a) => (
              <div key={a.id} className="music-side-addon-group">
                <span className="music-side-addon">{a.icon && <img src={a.icon} alt="" />}{a.name}</span>
                {a.catalogs.map((c) => (
                  <a key={c.key} className="music-side-item music-side-cat" {...linkProps(`/music/catalog?addon=${a.id}&id=${encodeURIComponent(c.key)}&name=${encodeURIComponent(c.name)}`)} aria-current={section === 'catalog' && params.get('addon') === a.id && params.get('id') === c.key ? 'page' : undefined}>{c.name}</a>
                ))}
              </div>
            ))}
          </>
        )}
      </aside>

      <div className="music-main">
        {section === 'search' && musicLibs.length > 0 && addons.length > 0 && (
          <div className="filterbar music-filter" role="radiogroup" aria-label="Bron">
            {([['all', 'Alles'], ['plex', 'Plex'], ['addons', 'Addons']] as [SourceFilter, string][]).map(([f, l]) => (
              <button key={f} className="chip" role="radio" aria-checked={filter === f} aria-pressed={filter === f} onClick={() => setFilter(f)}>{l}</button>
            ))}
          </div>
        )}
        {!musicLibs.length && !addons.length && (
          <p className="placeholder-row">Nog geen muziek. Voeg een muziekbibliotheek toe in Plex, of een muziek-addon bij Instellingen → Addons.</p>
        )}
        {section === 'home' && <MusicHome name={me.profile?.title ?? ''} plex={plex} musicLibs={musicLibs.map((l) => l.key)} addons={addons} lib={lib} />}
        {section === 'discover' && <DiscoverPage />}
        {section === 'chart' && <ChartPage key={params.get('src') ?? ''} src={params.get('src') ?? 'apple-nl'} />}
        {section === 'genre' && <GenrePage key={params.get('id')} id={params.get('id') ?? '0'} name={params.get('name') ?? ''} />}
        {section === 'dplaylist' && <DzPlaylistPage key={params.get('id')} id={params.get('id') ?? ''} />}
        {section === 'search' && <MusicSearch initial={params.get('q') ?? ''} plex={plex} musicLibs={musicLibs.map((l) => l.key)} showPlex={showPlex} showAddons={showAddons} />}
        {section === 'albums' && <PlexGrid plex={plex} libs={musicLibs.map((l) => l.key)} type={9} />}
        {section === 'artists' && <PlexGrid plex={plex} libs={musicLibs.map((l) => l.key)} type={8} />}
        {section === 'favorites' && <MinePage plex={plex} lib={lib} />}
        {section === 'playlist' && <OwnPlaylistPage key={params.get('id')} id={params.get('id') ?? ''} />}
        {section === 'plexplaylist' && <PlexPlaylistPage key={params.get('id')} plex={plex} id={params.get('id') ?? ''} />}
        {section === 'album' && <AlbumPage key={params.toString()} plex={plex} params={params} />}
        {section === 'artist' && <ArtistPage key={params.toString()} plex={plex} params={params} />}
        {section === 'addonplaylist' && <AddonListPage key={params.toString()} kind="playlist" addon={params.get('addon') ?? ''} id={params.get('id') ?? ''} />}
        {section === 'catalog' && <CatalogPage key={params.toString()} addon={params.get('addon') ?? ''} id={params.get('id') ?? ''} name={params.get('name') ?? ''} />}
        <div className="footer-space music-footer" />
      </div>
    </main>
  );
}

// ---------- Onderdelen ----------

function CardRow({ title, cards, more }: { title: string; cards: Card[] | null; more?: { label: string; href: string } }) {
  if (cards && !cards.length) return null;
  return (
    <section className="music-row" aria-label={title}>
      <div className="music-row-head">
        <h2>{title}</h2>
        {more && <a className="row-more" {...linkProps(more.href)}>{more.label}</a>}
      </div>
      {!cards ? <div className="row-skeleton" /> : (
        <div className="music-cards" data-focus-group>
          {cards.map((c) => <MusicCard key={c.key} c={c} />)}
        </div>
      )}
    </section>
  );
}

function MusicCard({ c }: { c: Card }) {
  return (
    <span className="music-card-wrap">
      <a className={`music-card${c.round ? ' round' : ''}`} {...linkProps(c.href)}>
        <span className="music-card-art" style={c.art ? { backgroundImage: `url("${c.art}")` } : undefined}>{!c.art && <span aria-hidden="true">♪</span>}</span>
        <strong className="music-card-title">{c.title}</strong>
        <span className="subtle music-card-sub">{c.sub}</span>
        {c.source && <span className="music-src">{c.source}</span>}
      </a>
      {c.save && <SaveButton item={c.save} />}
    </span>
  );
}

interface CatalogItem { type: string; id: string; addonId: string; addonName: string; title?: string; name?: string; artist?: string; artwork: string | null; year?: string | null; trackCount?: number | null; creator?: string | null; genres?: string[]; duration?: number | null; album?: string | null; media?: string | null; format?: string | null }

function catalogCard(x: CatalogItem): Card | null {
  if (x.type === 'album') return addonAlbumCard(x as unknown as AAlbum);
  if (x.type === 'artist') return addonArtistCard({ ...(x as unknown as AArtist), name: x.name ?? x.title ?? '', genres: x.genres ?? [] });
  if (x.type === 'playlist') return addonPlaylistCard(x as unknown as APlaylist);
  return null;
}

function AddonCatalogRow({ addon, catalog }: { addon: MusicAddon; catalog: { key: string; name: string } }) {
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    call<{ items: CatalogItem[] }>(`/api/music/${addon.id}/catalog?id=${encodeURIComponent(catalog.key)}`)
      .then((r) => setItems(r.items))
      .catch((e: unknown) => {
        setItems([]);
        setError(errText(e, 'laden mislukt'));
      });
  }, [addon.id, catalog.key]);
  if (error) return <p className="subtle hint">{catalog.name} ({addon.name}) kon niet geladen worden: {error}</p>;
  if (items && !items.length) return <p className="subtle hint">{catalog.name} ({addon.name}) is leeg.</p>;
  const tracks = items?.filter((x) => x.type === 'track') ?? [];
  const more = { label: 'Alles', href: `/music/catalog?addon=${addon.id}&id=${encodeURIComponent(catalog.key)}&name=${encodeURIComponent(catalog.name)}` };
  if (items && tracks.length > items.length / 2) {
    const list = tracks.map((t) => ({ ...t, source: 'addon' as const, title: t.title ?? '', artist: t.artist ?? '' })) as MusicTrack[];
    return (
      <section className="music-row" aria-label={catalog.name}>
        <div className="music-row-head"><h2>{catalog.name}</h2><span className="subtle">catalogus uit {addon.name}</span><a className="row-more" {...linkProps(more.href)}>{more.label}</a></div>
        <TrackList tracks={list.slice(0, 8)} compact />
      </section>
    );
  }
  return <CardRow title={`${catalog.name} · ${addon.name}`} cards={items ? items.map(catalogCard).filter((c): c is Card => !!c) : null} more={more} />;
}

function CatalogPage({ addon, id, name }: { addon: string; id: string; name: string }) {
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    call<{ items: CatalogItem[] }>(`/api/music/${addon}/catalog?id=${encodeURIComponent(id)}`).then((r) => setItems(r.items)).catch((e: unknown) => setError(errText(e, 'Laden mislukt.')));
  }, [addon, id]);
  if (error) return <p className="placeholder-row error">{error}</p>;
  const tracks = (items ?? []).filter((x) => x.type === 'track').map((t) => ({ ...t, source: 'addon' as const, title: t.title ?? '', artist: t.artist ?? '' })) as MusicTrack[];
  const cards = (items ?? []).map(catalogCard).filter((c): c is Card => !!c);
  return (
    <>
      <header className="music-hello"><h1>{name || 'Catalogus'}</h1></header>
      {!items && <div className="row-skeleton" />}
      {tracks.length > 0 && <TrackList tracks={tracks} />}
      {cards.length > 0 && <div className="music-grid">{cards.map((c) => <MusicCard key={c.key} c={c} />)}</div>}
    </>
  );
}

function MusicSearch({ initial, plex, musicLibs, showPlex, showAddons }: { initial: string; plex: PlexClient; musicLibs: string[]; showPlex: boolean; showAddons: boolean }) {
  const [q, setQ] = useState(initial);
  const [term, setTerm] = useState(initial);
  const [plexRes, setPlexRes] = useState<{ tracks: MusicTrack[]; albums: Card[]; artists: Card[] } | null>(null);
  const [addonRes, setAddonRes] = useState<{ tracks: ATrack[]; albums: AAlbum[]; artists: AArtist[]; playlists: APlaylist[]; errors: { addon: string }[] } | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setTerm(q.trim()), 350);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (term.length < 2) {
      setPlexRes(null);
      setAddonRes(null);
      return;
    }
    navigate(`/music/search?q=${encodeURIComponent(term)}`, { replace: true });
    if (showPlex) {
      setPlexRes(null);
      Promise.all(musicLibs.map((k) => plex.get<Container<{ Hub?: { type: string; Metadata?: PlexItem[] }[] }>>('/hubs/search', { query: term, sectionId: k, limit: 20 }).catch(() => null)))
        .then((rs) => {
          const hubs = rs.flatMap((r) => r?.MediaContainer.Hub ?? []);
          const of = (type: string) => hubs.filter((h) => h.type === type).flatMap((h) => h.Metadata ?? []);
          setPlexRes({ tracks: of('track').map((t) => fromPlexTrack(plex, t)), albums: of('album').map((a) => plexAlbumCard(plex, a)), artists: of('artist').map((a) => plexArtistCard(plex, a)) });
        });
    }
    if (showAddons) {
      setAddonRes(null);
      call<NonNullable<typeof addonRes>>(`/api/music/search?q=${encodeURIComponent(term)}`).then(setAddonRes).catch(() => setAddonRes({ tracks: [], albums: [], artists: [], playlists: [], errors: [] }));
    }
  }, [term, showPlex, showAddons]);

  const tracks = [...(plexRes?.tracks ?? []), ...(addonRes?.tracks ?? [])];
  const artists = [...(plexRes?.artists ?? []), ...(addonRes?.artists.map(addonArtistCard) ?? [])];
  const albums = [...(plexRes?.albums ?? []), ...(addonRes?.albums.map(addonAlbumCard) ?? [])];
  const playlists = addonRes?.playlists.map(addonPlaylistCard) ?? [];
  const busy = term.length >= 2 && ((showPlex && !plexRes) || (showAddons && !addonRes));

  return (
    <>
      <form className="music-search" role="search" onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }}>
        <input className="text-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Artiest, album of nummer" aria-label="Zoeken in muziek" />
      </form>
      {busy && <p className="subtle" aria-busy="true">Zoeken in {[showPlex ? 'Plex' : null, showAddons ? 'je addons' : null].filter(Boolean).join(' en ')}…</p>}
      {term.length >= 2 && !busy && !tracks.length && !artists.length && !albums.length && !playlists.length && <p className="placeholder-row">Niets gevonden voor “{term}”.</p>}
      {tracks.length > 0 && (
        <section className="music-row" aria-label="Nummers">
          <div className="music-row-head"><h2>Nummers</h2></div>
          <TrackList tracks={tracks.slice(0, 30)} showSource />
        </section>
      )}
      <CardRow title="Artiesten" cards={artists.length ? artists : []} />
      <CardRow title="Albums" cards={albums.length ? albums : []} />
      <CardRow title="Playlists" cards={playlists.length ? playlists : []} />
      {addonRes?.errors.length ? <p className="subtle hint">Niet bereikbaar: {addonRes.errors.map((e) => e.addon).join(', ')}</p> : null}
    </>
  );
}

function PlexGrid({ plex, libs, type }: { plex: PlexClient; libs: string[]; type: 8 | 9 }) {
  const [cards, setCards] = useState<Card[] | null>(null);
  const [size, setSize] = useState(120);
  useEffect(() => {
    Promise.all(libs.map((k) => plex.get<Container<{ Metadata?: PlexItem[] }>>(`/library/sections/${k}/all`, { type, sort: 'titleSort', 'X-Plex-Container-Start': 0, 'X-Plex-Container-Size': size }).catch(() => null)))
      .then((rs) => setCards(rs.flatMap((r) => r?.MediaContainer.Metadata ?? []).map((a) => (type === 9 ? plexAlbumCard(plex, a) : plexArtistCard(plex, a)))));
  }, [libs.join(), type, size]);
  return (
    <>
      <header className="music-hello"><h1>{type === 9 ? 'Albums' : 'Artiesten'}</h1><span className="subtle">Plex</span></header>
      {!cards ? <div className="row-skeleton" /> : <div className="music-grid">{cards.map((c) => <MusicCard key={c.key} c={c} />)}</div>}
      {cards && cards.length >= size && <div className="form-actions"><button className="btn" onClick={() => setSize((s) => s + 120)}>Meer laden</button></div>}
    </>
  );
}

function AlbumPage({ plex, params }: { plex: PlexClient; params: URLSearchParams }) {
  const src = params.get('src');
  const id = params.get('id') ?? '';
  const [album, setAlbum] = useState<{ title: string; artist: string; artistHref?: string; art?: string | null; year?: string | null; tracks: MusicTrack[]; source: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (src === 'plex') {
      Promise.all([plex.metadata(id), plex.get<Container<{ Metadata?: PlexItem[] }>>(`/library/metadata/${id}/children`)])
        .then(([a, c]) => setAlbum({
          title: a.title, artist: a.parentTitle ?? '', artistHref: a.parentRatingKey ? `/music/artist?src=plex&id=${a.parentRatingKey}` : undefined,
          art: plex.image(a.thumb, 600, 600), year: a.year ? String(a.year) : null, source: 'Plex',
          tracks: (c.MediaContainer.Metadata ?? []).map((t) => fromPlexTrack(plex, t)),
        }))
        .catch(() => setError('Album laden mislukt.'));
    } else if (src === 'deezer') {
      call<{ album: CatAlbum & { tracks: MusicTrack[] } }>(`/api/music/cat/album/${encodeURIComponent(id)}`)
        .then(({ album: a }) => setAlbum({ title: a.title, artist: a.artist, art: a.artwork, year: a.year, source: 'Deezer', tracks: a.tracks }))
        .catch((e: unknown) => setError(errText(e, 'Album laden mislukt.')));
    } else {
      const addon = params.get('addon') ?? '';
      call<{ album: AAlbum & { tracks: ATrack[] } }>(`/api/music/${addon}/album/${encodeURIComponent(id)}`)
        .then(({ album: a }) => setAlbum({ title: a.title, artist: a.artist, art: a.artwork, year: a.year, source: a.addonName, tracks: a.tracks }))
        .catch((e: unknown) => setError(errText(e, 'Album laden mislukt.')));
    }
  }, [src, id]);

  if (error) return <p className="placeholder-row error">{error}</p>;
  if (!album) return <div className="row-skeleton" />;
  const total = album.tracks.reduce((n, t) => n + (t.duration ?? 0), 0);
  return (
    <>
      <header className="music-hero">
        <span className="music-hero-art" style={album.art ? { backgroundImage: `url("${album.art}")` } : undefined} />
        <div>
          <span className="eyebrow">Album · {album.source}</span>
          <h1>{album.title}</h1>
          <p className="subtle">
            {album.artistHref ? <a {...linkProps(album.artistHref)}>{album.artist}</a> : album.artist}
            {album.year ? ` · ${album.year}` : ''} · {album.tracks.length} nummers{total ? ` · ${fmtTime(total)}` : ''}
          </p>
          <div className="hero-actions" data-focus-group>
            <button className="btn btn-primary" data-autofocus disabled={!album.tracks.length} onClick={() => playQueue(album.tracks, 0)}>▶ Afspelen</button>
            <button className="btn" disabled={!album.tracks.length} onClick={() => playQueue([...album.tracks].sort(() => Math.random() - 0.5), 0)}>⤮ Shuffle</button>
            <SaveButton big item={{ kind: 'album', src: src === 'plex' ? 'plex' : src === 'deezer' ? 'deezer' : 'addon', addonId: src === 'addon' || !src ? params.get('addon') ?? undefined : undefined, id, title: album.title, sub: [album.artist, album.year].filter(Boolean).join(' · '), artwork: src === 'plex' ? null : album.art ?? null }} />
            <AddAllButton tracks={album.tracks} name={album.title} />
          </div>
        </div>
      </header>
      <TrackList tracks={album.tracks} numbered showSource={album.source === 'Deezer'} />
    </>
  );
}

function ArtistPage({ plex, params }: { plex: PlexClient; params: URLSearchParams }) {
  const src = params.get('src');
  const id = params.get('id') ?? '';
  const [artist, setArtist] = useState<{ name: string; art?: string | null; bio?: string | null; top: MusicTrack[]; albums: Card[]; related?: Card[]; source: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (src === 'plex') {
      Promise.all([
        plex.metadata(id),
        plex.get<Container<{ Metadata?: PlexItem[] }>>(`/library/metadata/${id}/children`),
        plex.get<Container<{ Metadata?: PlexItem[] }>>(`/library/metadata/${id}/allLeaves`).catch(() => null),
      ]).then(([a, c, leaves]) => {
        const all = leaves?.MediaContainer.Metadata ?? [];
        // "Populair": meest beluisterd, anders de eerste nummers
        const top = [...all].sort((x, y) => (y.viewCount ?? 0) - (x.viewCount ?? 0) || (y.audienceRating ?? 0) - (x.audienceRating ?? 0)).slice(0, 10);
        setArtist({ name: a.title, art: plex.image(a.thumb, 600, 600), bio: a.summary ?? null, source: 'Plex', top: top.map((t) => fromPlexTrack(plex, t)), albums: (c.MediaContainer.Metadata ?? []).map((x) => plexAlbumCard(plex, x)) });
      }).catch(() => setError('Artiest laden mislukt.'));
    } else if (src === 'deezer') {
      call<{ artist: CatArtist & { top: MusicTrack[]; albums: CatAlbum[]; related: CatArtist[] } }>(`/api/music/cat/artist/${encodeURIComponent(id)}`)
        .then(({ artist: a }) => setArtist({ name: a.name, art: a.artwork, bio: null, source: 'Deezer', top: a.top, albums: a.albums.map(dzAlbumCard), related: a.related.map(dzArtistCard) }))
        .catch((e: unknown) => setError(errText(e, 'Artiest laden mislukt.')));
    } else {
      const addon = params.get('addon') ?? '';
      call<{ artist: AArtist & { bio: string | null; topTracks: ATrack[]; albums: AAlbum[] } }>(`/api/music/${addon}/artist/${encodeURIComponent(id)}`)
        .then(({ artist: a }) => setArtist({ name: a.name, art: a.artwork, bio: a.bio, source: a.addonName, top: a.topTracks, albums: a.albums.map(addonAlbumCard) }))
        .catch((e: unknown) => setError(errText(e, 'Artiest laden mislukt.')));
    }
  }, [src, id]);

  if (error) return <p className="placeholder-row error">{error}</p>;
  if (!artist) return <div className="row-skeleton" />;
  return (
    <>
      <header className="music-hero">
        <span className="music-hero-art round" style={artist.art ? { backgroundImage: `url("${artist.art}")` } : undefined} />
        <div>
          <span className="eyebrow">Artiest · {artist.source}</span>
          <h1>{artist.name}</h1>
          {artist.bio && <p className="subtle clamp-3">{artist.bio}</p>}
          <div className="hero-actions" data-focus-group>
            <button className="btn btn-primary" data-autofocus disabled={!artist.top.length} onClick={() => playQueue(artist.top, 0)}>▶ Populair afspelen</button>
            <SaveButton big item={{ kind: 'artist', src: src === 'plex' ? 'plex' : src === 'deezer' ? 'deezer' : 'addon', addonId: src === 'addon' || !src ? params.get('addon') ?? undefined : undefined, id, title: artist.name, artwork: src === 'plex' ? null : artist.art ?? null }} />
            <AddAllButton tracks={artist.top} name={`${artist.name} · populair`} />
          </div>
        </div>
      </header>
      {artist.top.length > 0 && (
        <section className="music-row" aria-label="Populair">
          <div className="music-row-head"><h2>Populair</h2></div>
          <TrackList tracks={artist.top} numbered showSource={artist.source === 'Deezer'} />
        </section>
      )}
      <CardRow title="Albums" cards={artist.albums} />
      {artist.related && <CardRow title="Vergelijkbare artiesten" cards={artist.related} />}
    </>
  );
}

function AddonListPage({ kind, addon, id }: { kind: 'playlist'; addon: string; id: string }) {
  const [list, setList] = useState<(APlaylist & { description: string | null; tracks: ATrack[] }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    call<{ playlist: APlaylist & { description: string | null; tracks: ATrack[] } }>(`/api/music/${addon}/${kind}/${encodeURIComponent(id)}`)
      .then((r) => setList(r.playlist)).catch((e: unknown) => setError(errText(e, 'Laden mislukt.')));
  }, [addon, id]);
  if (error) return <p className="placeholder-row error">{error}</p>;
  if (!list) return <div className="row-skeleton" />;
  return <TracksPage title={list.title} sub={[list.creator, list.addonName].filter(Boolean).join(' · ')} art={list.artwork} tracks={list.tracks} extra={<PlaylistExtras tracks={list.tracks} name={list.title} save={{ kind: 'playlist', src: 'addon', addonId: addon, id, title: list.title, sub: list.creator, artwork: list.artwork }} />} />;
}

function OwnPlaylistPage({ id }: { id: string }) {
  const { plex } = useApp();
  const [list, setList] = useState<{ id: string; name: string; tracks: MusicTrack[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [plexMsg, setPlexMsg] = useState<string | null>(null);
  const load = () => call<{ playlist: { id: string; name: string; tracks: MusicTrack[] } }>(`/api/music/playlists/${id}`).then((r) => setList(r.playlist)).catch(() => setError('Playlist niet gevonden.'));
  useEffect(() => {
    void load();
  }, [id]);
  if (error) return <p className="placeholder-row error">{error}</p>;
  if (!list) return <div className="row-skeleton" />;
  const tracks = list.tracks.map((t) => hydrate(plex, t));
  return (
    <TracksPage
      title={list.name}
      sub="Jouw playlist"
      tracks={tracks}
      empty="Nog leeg. Voeg nummers toe met ＋ bij een nummer."
      onRemove={async (i) => {
        await call(`/api/music/playlists/${id}`, { method: 'PATCH', body: JSON.stringify({ remove: [i] }) }).catch(() => undefined);
        void load();
        libraryChanged();
      }}
      extra={confirm ? (
        <>
          <button className="btn btn-danger" onClick={async () => { await call(`/api/music/playlists/${id}`, { method: 'DELETE' }).catch(() => undefined); libraryChanged(); navigate('/music'); }}>Ja, verwijderen</button>
          <button className="btn" onClick={() => setConfirm(false)}>Annuleren</button>
        </>
      ) : (
        <>
          {tracks.some((t) => t.source === 'plex' && t.ratingKey) && <button className="btn" onClick={async () => setPlexMsg(await exportToPlex(plex, list.name, tracks))}>Ook in Plex zetten</button>}
          <button className="btn btn-ghost" onClick={() => setConfirm(true)}>Playlist verwijderen</button>
          {plexMsg && <span className="subtle" role="status">{plexMsg}</span>}
        </>
      )}
    />
  );
}

function PlexPlaylistsSide({ current }: { current: string | null }) {
  const { plex } = useApp();
  const [lists, setLists] = useState<{ ratingKey: string; title: string }[]>([]);
  useEffect(() => {
    plex.get<Container<{ Metadata?: { ratingKey: string; title: string; smart?: boolean }[] }>>('/playlists', { playlistType: 'audio' })
      .then((r) => setLists(r.MediaContainer.Metadata ?? [])).catch(() => setLists([]));
  }, []);
  return (
    <>
      {lists.map((p) => (
        <a key={p.ratingKey} className="music-side-item" {...linkProps(`/music/plexplaylist?id=${p.ratingKey}`)} aria-current={current === p.ratingKey ? 'page' : undefined}>{p.title}<span className="subtle"> · Plex</span></a>
      ))}
    </>
  );
}

function PlexPlaylistPage({ plex, id }: { plex: PlexClient; id: string }) {
  const [list, setList] = useState<{ title: string; tracks: MusicTrack[] } | null>(null);
  useEffect(() => {
    Promise.all([plex.metadata(id), plex.get<Container<{ Metadata?: PlexItem[] }>>(`/playlists/${id}/items`)])
      .then(([p, r]) => setList({ title: p.title, tracks: (r.MediaContainer.Metadata ?? []).filter((t) => t.type === 'track').map((t) => fromPlexTrack(plex, t)) }))
      .catch(() => setList({ title: 'Playlist', tracks: [] }));
  }, [id]);
  if (!list) return <div className="row-skeleton" />;
  return <TracksPage title={list.title} sub="Plex-playlist" tracks={list.tracks} extra={<AddAllButton tracks={list.tracks} name={list.title} />} />;
}

function TracksPage({ title, sub, art, tracks, empty, onRemove, extra }: { title: string; sub?: string; art?: string | null; tracks: MusicTrack[] | null; empty?: string; onRemove?: (i: number) => void; extra?: ReactNode }) {
  if (!tracks) return <div className="row-skeleton" />;
  const total = tracks.reduce((n, t) => n + (t.duration ?? 0), 0);
  return (
    <>
      <header className="music-hero">
        <span className="music-hero-art" style={art ?? tracks[0]?.artwork ? { backgroundImage: `url("${art ?? tracks[0]?.artwork}")` } : undefined} />
        <div>
          {sub && <span className="eyebrow">{sub}</span>}
          <h1>{title}</h1>
          <p className="subtle">{tracks.length} nummers{total ? ` · ${fmtTime(total)}` : ''}</p>
          <div className="hero-actions" data-focus-group>
            <button className="btn btn-primary" disabled={!tracks.length} onClick={() => playQueue(tracks, 0)}>▶ Afspelen</button>
            <button className="btn" disabled={!tracks.length} onClick={() => playQueue([...tracks].sort(() => Math.random() - 0.5), 0)}>⤮ Shuffle</button>
            {extra}
          </div>
        </div>
      </header>
      {!tracks.length && empty && <p className="placeholder-row">{empty}</p>}
      <TrackList tracks={tracks} showSource onRemove={onRemove} />
    </>
  );
}

function TrackList({ tracks, numbered, compact, showSource, onRemove }: { tracks: MusicTrack[]; numbered?: boolean; compact?: boolean; showSource?: boolean; onRemove?: (i: number) => void }) {
  const s = useMusic();
  const now = s.queue[s.index];
  const [menu, setMenu] = useState<number | null>(null);
  const hasCatalog = useMemo(() => tracks.some((t) => t.source === 'catalog'), [tracks]);
  useResolveAll(showSource && hasCatalog ? tracks : null);
  return (
    <ol className={`track-list${compact ? ' compact' : ''}`} data-focus-group>
      {tracks.map((t, i) => {
        const active = !!now && ((now.id === t.id && now.source === t.source && (now.addonId ?? '') === (t.addonId ?? '')) || (t.source === 'catalog' && now.title === t.title && now.artist === t.artist));
        const via = t.source === 'catalog' ? viaLabel(t) : null;
        return (
          <li key={`${t.source}:${t.addonId ?? ''}:${t.id}:${i}`} className={`track${active ? ' playing' : ''}`}>
            <button className="track-main" onClick={() => playQueue(tracks, i)} aria-label={`${t.title} van ${t.artist} afspelen`}>
              <span className="track-no">{active && s.playing ? '♪' : numbered ? i + 1 : <span className="track-art" style={t.artwork ? { backgroundImage: `url("${t.artwork}")` } : undefined} />}</span>
              <span className="track-text">
                <strong>{t.title}</strong>
                <span className="subtle">{[t.artist, !numbered ? t.album : null].filter(Boolean).join(' · ')}</span>
              </span>
              {showSource && (via ? <span className={`music-src via-${via.tone}`}>{via.text}</span> : <span className="music-src">{t.source === 'plex' ? 'Plex' : t.addonName ?? 'Addon'}</span>)}
              {t.format && <span className="track-format subtle">{t.format}</span>}
              <span className="track-time subtle">{t.duration ? fmtTime(t.duration) : ""}</span>
            </button>
            <button className="mp-btn" aria-label={`Meer voor ${t.title}`} aria-expanded={menu === i} onClick={() => setMenu(menu === i ? null : i)}>＋</button>
            {menu === i && <TrackMenu t={t} onClose={() => setMenu(null)} onRemove={onRemove ? () => { onRemove(i); setMenu(null); } : undefined} />}
          </li>
        );
      })}
    </ol>
  );
}

function TrackMenu({ t, onClose, onRemove }: { t: MusicTrack; onClose: () => void; onRemove?: () => void }) {
  const lib = useLibrary();
  const [msg, setMsg] = useState<string | null>(null);
  const isFav = useMemo(() => !!lib?.favorites.some((f) => f.id === t.id && f.source === t.source && (f.addonId ?? '') === (t.addonId ?? '')), [lib, t]);
  const done = (m: string) => {
    setMsg(m);
    window.setTimeout(onClose, 900);
  };
  return (
    <div className="track-menu" role="menu">
      <button role="menuitem" onClick={() => { playNext(t); done('Speelt hierna'); }}>Hierna afspelen</button>
      <button role="menuitem" onClick={() => { addToQueue(t); done('In de wachtrij'); }}>Aan wachtrij toevoegen</button>
      <button role="menuitem" onClick={async () => { await call('/api/music/favorites', { method: 'PUT', body: JSON.stringify({ track: storable(t), on: !isFav }) }).catch(() => undefined); libraryChanged(); done(isFav ? 'Uit favorieten' : '♥ Favoriet'); }}>{isFav ? 'Uit favorieten' : 'Aan favorieten toevoegen'}</button>
      {lib?.playlists.map((p) => (
        <button key={p.id} role="menuitem" onClick={async () => { await call(`/api/music/playlists/${p.id}`, { method: 'PATCH', body: JSON.stringify({ add: [storable(t)] }) }).catch(() => undefined); libraryChanged(); done(`Toegevoegd aan ${p.name}`); }}>＋ {p.name}</button>
      ))}
      {onRemove && <button role="menuitem" onClick={onRemove}>Uit deze playlist</button>}
      {msg && <span className="subtle" role="status">{msg}</span>}
    </div>
  );
}

/** Bij een playlist van een addon of Deezer: volgen (standaard, blijft meeveranderen) of kopiëren als eigen playlist. */
function PlaylistExtras({ tracks, name, save }: { tracks: MusicTrack[]; name: string; save: Saved }) {
  return (
    <>
      <SaveButton big item={save} />
      <button className="btn" disabled={!tracks.length} onClick={async () => {
        const r = await call<{ playlist: { id: string } }>('/api/music/playlists', { method: 'POST', body: JSON.stringify({ name, tracks: tracks.map(storable) }) }).catch(() => null);
        libraryChanged();
        if (r) navigate(`/music/playlist?id=${r.playlist.id}`);
      }}>Kopiëren als eigen playlist</button>
      <AddAllButton tracks={tracks} name={name} />
    </>
  );
}

type MineTab = 'tracks' | 'albums' | 'artists' | 'playlists';

/** Mijn muziek: favoriete nummers en bewaarde albums, artiesten en (gevolgde en eigen) playlists, uit elke bron. */
function MinePage({ plex, lib }: { plex: PlexClient; lib: Library | null }) {
  const [tab, setTab] = useState<MineTab>(() => (new URLSearchParams(location.search).get('tab') as MineTab) || 'tracks');
  if (!lib) return <div className="row-skeleton" />;
  const of = (k: Saved['kind']) => lib.saved.filter((x) => x.kind === k).map((x) => savedCard(plex, x));
  const own: Card[] = lib.playlists.map((p) => ({ key: `own:${p.id}`, title: p.name, sub: `${p.count} nummers · eigen playlist`, art: p.artwork ?? undefined, href: `/music/playlist?id=${p.id}` }));
  const tabs: [MineTab, string, number][] = [['tracks', 'Nummers', lib.favorites.length], ['albums', 'Albums', of('album').length], ['artists', 'Artiesten', of('artist').length], ['playlists', 'Playlists', of('playlist').length + own.length]];
  const grid = (cards: Card[], empty: string) => (cards.length ? <div className="music-grid">{cards.map((c) => <MusicCard key={c.key} c={c} />)}</div> : <p className="placeholder-row">{empty}</p>);
  return (
    <>
      <header className="music-hello"><h1>Mijn muziek</h1></header>
      <div className="filterbar" role="tablist" aria-label="Mijn muziek">
        {tabs.map(([id, label, n]) => <button key={id} className="chip" role="tab" aria-selected={tab === id} aria-pressed={tab === id} onClick={() => setTab(id)}>{label}<span className="subtle"> · {n}</span></button>)}
      </div>
      {tab === 'tracks' && (lib.favorites.length ? <TrackList tracks={lib.favorites.map((t) => hydrate(plex, t))} showSource /> : <p className="placeholder-row">Tik op ♡ of ⋯ bij een nummer om het hier te bewaren.</p>)}
      {tab === 'albums' && grid(of('album'), 'Tik op ♡ bij een album (ook in de zoekresultaten) om het hier te bewaren.')}
      {tab === 'artists' && grid(of('artist'), 'Tik op ♡ bij een artiest om die hier te bewaren.')}
      {tab === 'playlists' && grid([...own, ...of('playlist')], 'Volg een playlist (♡) of maak er zelf een met ＋ in het menu.')}
    </>
  );
}

/** Eigen playlist die alleen Plex-nummers heeft: ook in Plex zetten (zie je terug in Plexamp). */
async function exportToPlex(plex: PlexClient, name: string, tracks: MusicTrack[]): Promise<string> {
  const keys = tracks.map((t) => t.ratingKey).filter((k): k is string => !!k);
  if (!keys.length) return 'Geen Plex-nummers in deze playlist.';
  const id = await plex.get<{ MediaContainer: { machineIdentifier?: string } }>('/identity').then((r) => r.MediaContainer.machineIdentifier).catch(() => null);
  if (!id) return 'Plex is niet bereikbaar.';
  const url = plex.url('/playlists', { type: 'audio', title: name, smart: 0, uri: `server://${id}/com.plexapp.plugins.library/library/metadata/${keys.join(',')}` }, true);
  const res = await fetch(url, { method: 'POST', headers: { Accept: 'application/json' } }).catch(() => null);
  return res?.ok ? `In Plex gezet${keys.length < tracks.length ? ` (${keys.length} van ${tracks.length}: de rest staat niet in Plex)` : ''}.` : 'In Plex zetten mislukt.';
}

function NewPlaylistButton() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  if (!open) return <button className="mp-btn" onClick={() => setOpen(true)} aria-label="Nieuwe playlist">＋</button>;
  return (
    <form className="music-new-pl" onSubmit={async (e) => {
      e.preventDefault();
      if (!name.trim()) return;
      const r = await call<{ playlist: { id: string } }>('/api/music/playlists', { method: 'POST', body: JSON.stringify({ name }) }).catch(() => null);
      setOpen(false);
      setName('');
      libraryChanged();
      if (r) navigate(`/music/playlist?id=${r.playlist.id}`);
    }}>
      <input className="text-input text-input-sm" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Naam" aria-label="Naam van de playlist" onBlur={() => !name && setOpen(false)} />
    </form>
  );
}

// ---------- Catalogi: Deezer, Apple Music, Last.fm ----------

interface CatAlbum { id: string; title: string; artist: string; artwork: string | null; year: string | null }
interface CatArtist { id: string; name: string; artwork: string | null }
interface CatPlaylist { id: string; title: string; artwork: string | null; sub: string | null }
interface CatGenre { id: string; name: string; artwork: string | null }
interface Chart { title: string; provider: string; tracks: MusicTrack[] }

const dzAlbumCard = (a: CatAlbum): Card => ({ key: `dz:${a.id}`, title: a.title, sub: [a.artist, a.year].filter(Boolean).join(' · '), art: a.artwork ?? undefined, href: `/music/album?src=deezer&id=${a.id}`, save: { kind: 'album', src: 'deezer', id: a.id, title: a.title, sub: a.artist, artwork: a.artwork } });
const dzArtistCard = (a: CatArtist): Card => ({ key: `dza:${a.id}`, title: a.name, sub: 'Artiest', art: a.artwork ?? undefined, href: `/music/artist?src=deezer&id=${a.id}`, round: true, save: { kind: 'artist', src: 'deezer', id: a.id, title: a.name, artwork: a.artwork } });
const dzPlaylistCard = (p: CatPlaylist): Card => ({ key: `dzp:${p.id}`, title: p.title, sub: p.sub ?? 'Playlist', art: p.artwork ?? undefined, href: `/music/dplaylist?id=${p.id}`, save: { kind: 'playlist', src: 'deezer', id: p.id, title: p.title, sub: p.sub, artwork: p.artwork } });

const catCache = new Map<string, Promise<unknown>>();
/** Lijsten uit de catalogus één keer per bezoek ophalen (de server cachet ook). */
function cat<T>(path: string): Promise<T> {
  let p = catCache.get(path);
  if (!p) {
    p = call<T>(path).catch((e: unknown) => {
      catCache.delete(path);
      throw e;
    });
    catCache.set(path, p);
  }
  return p as Promise<T>;
}

function useCat<T>(path: string | null): { data: T | null; error: string | null } {
  const [state, setState] = useState<{ data: T | null; error: string | null }>({ data: null, error: null });
  useEffect(() => {
    if (!path) return;
    let off = false;
    setState({ data: null, error: null });
    cat<T>(path).then((d) => !off && setState({ data: d, error: null })).catch((e: unknown) => !off && setState({ data: null, error: errText(e, 'Laden mislukt') }));
    return () => {
      off = true;
    };
  }, [path]);
  return state;
}

// ---------- Rijen op muziek-Home ----------

interface HomeRow { id: string; kind: string; on: boolean; title?: string; param?: string }

const CHART_NAMES: Record<string, string> = {
  'apple-nl': 'Top 100 Nederland', 'apple-us': 'Top 100 Verenigde Staten', 'apple-gb': 'Top 100 Verenigd Koninkrijk',
  deezer: 'Deezer Top', 'lastfm-nl': 'Last.fm Top Nederland', lastfm: 'Last.fm Top Wereld',
};

function defaultRows(addons: MusicAddon[]): HomeRow[] {
  return [
    { id: 'recent', kind: 'recent', on: true, title: 'Verder luisteren' },
    { id: 'chart:apple-nl', kind: 'chart', on: true, param: 'apple-nl', title: 'Top 100 Nederland' },
    { id: 'newAlbums', kind: 'newAlbums', on: true, title: 'Nieuwe albums' },
    { id: 'plexRecent', kind: 'plexRecent', on: true, title: 'Recent toegevoegd in Plex' },
    { id: 'genres', kind: 'genres', on: true, title: 'Genres' },
    { id: 'recs', kind: 'recs', on: true, title: 'Aanbevolen voor jou' },
    { id: 'dzPlaylists', kind: 'dzPlaylists', on: true, title: 'Populaire playlists' },
    { id: 'chart:deezer', kind: 'chart', on: false, param: 'deezer', title: 'Deezer Top' },
    ...addons.flatMap((a) => a.catalogs.map((c) => ({ id: `addon:${a.id}:${c.key}`.slice(0, 80), kind: 'addonCat', on: false, param: `${a.id}|${c.key}`, title: `${c.name} · ${a.name}` }))),
  ];
}

const ROW_SOURCE: Record<string, string> = { recent: 'tumedia', chart: '', newAlbums: 'Deezer', plexRecent: 'Plex', genres: 'Deezer', recs: 'Last.fm', dzPlaylists: 'Deezer', genre: 'Deezer', addonCat: 'Addon' };
const rowSource = (r: HomeRow) => (r.kind === 'chart' ? (r.param?.startsWith('apple') ? 'Apple Music' : r.param?.startsWith('lastfm') ? 'Last.fm' : 'Deezer') : ROW_SOURCE[r.kind] ?? '');

function MusicHome({ name, plex, musicLibs, addons, lib }: { name: string; plex: PlexClient; musicLibs: string[]; addons: MusicAddon[]; lib: Library | null }) {
  const [stored, setStored] = useState<HomeRow[] | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const hour = new Date().getHours();
  const greet = hour < 6 ? 'Goedenacht' : hour < 12 ? 'Goedemorgen' : hour < 18 ? 'Goedemiddag' : 'Goedenavond';

  useEffect(() => {
    call<{ rows: HomeRow[] | null }>('/api/music/rows').then((r) => setStored(r.rows)).catch(() => setStored(null));
  }, []);

  // Nieuwe addon-catalogi achteraan toevoegen (uit), verdwenen addons weglaten
  const rows = useMemo(() => {
    const defs = defaultRows(addons);
    if (!stored) return defs;
    const known = new Set(stored.map((r) => r.id));
    return [...stored.filter((r) => r.kind !== 'addonCat' || defs.some((d) => d.id === r.id)), ...defs.filter((d) => !known.has(d.id) && d.kind === 'addonCat')];
  }, [stored, addons]);

  const save = async (next: HomeRow[] | null) => {
    setStored(next);
    await call('/api/music/rows', { method: 'PUT', body: JSON.stringify({ rows: next }) }).catch(() => undefined);
  };

  return (
    <>
      <header className="music-hello">
        <h1>{greet}{name ? `, ${name}` : ''}</h1>
        <span style={{ flex: 1 }} />
        <button className="btn btn-sm" onClick={() => setEditing(true)}>☰ Rijen aanpassen</button>
      </header>
      {stored !== undefined && rows.filter((r) => r.on).map((r) => <HomeRowView key={r.id} row={r} plex={plex} musicLibs={musicLibs} addons={addons} lib={lib} />)}
      {editing && <RowsEditor rows={rows} onClose={() => setEditing(false)} onSave={(r) => void save(r)} />}
    </>
  );
}

function HomeRowView({ row, plex, musicLibs, addons, lib }: { row: HomeRow; plex: PlexClient; musicLibs: string[]; addons: MusicAddon[]; lib: Library | null }) {
  switch (row.kind) {
    case 'recent': {
      const recent = lib?.recent.map((t) => hydrate(plex, t)) ?? [];
      if (!recent.length) return null;
      return (
        <section className="music-row" aria-label="Verder luisteren">
          <div className="music-row-head"><h2>Verder luisteren</h2></div>
          <div className="music-quick" data-focus-group>
            {recent.slice(0, 8).map((t, i) => (
              <button key={`${t.source}:${t.id}`} className="music-quick-item" onClick={() => playQueue(recent, i)}>
                <span className="music-quick-art" style={t.artwork ? { backgroundImage: `url("${t.artwork}")` } : undefined} />
                <span><strong>{t.title}</strong><span className="subtle">{t.artist}</span></span>
              </button>
            ))}
          </div>
        </section>
      );
    }
    case 'chart': return <ChartPreview src={row.param ?? 'apple-nl'} />;
    case 'newAlbums': return <NewAlbumsRow />;
    case 'plexRecent': return musicLibs.length ? <PlexRecentRow plex={plex} musicLibs={musicLibs} /> : null;
    case 'genres': return <GenresRow />;
    case 'recs': return <RecsRow />;
    case 'dzPlaylists': return <DzPlaylistsRow />;
    case 'genre': return <GenreRow id={row.param ?? '0'} title={row.title ?? 'Genre'} />;
    case 'addonCat': {
      const [addonId, ...rest] = (row.param ?? '').split('|');
      const a = addons.find((x) => x.id === addonId);
      const c = a?.catalogs.find((x) => x.key === rest.join('|'));
      return a && c ? <AddonCatalogRow addon={a} catalog={c} /> : null;
    }
    default: return null;
  }
}

function RowHead({ title, badge, more }: { title: string; badge?: string; more?: { label: string; href: string } }) {
  return (
    <div className="music-row-head">
      <h2>{title}</h2>
      {badge && <span className="music-badge">{badge}</span>}
      {more && <a className="row-more" {...linkProps(more.href)}>{more.label}</a>}
    </div>
  );
}

function RowError({ title, error }: { title: string; error: string }) {
  return <p className="subtle hint">{title} kon niet geladen worden: {error}</p>;
}

function ChartPreview({ src }: { src: string }) {
  const { data, error } = useCat<Chart>(`/api/music/cat/chart?src=${src}`);
  const tracks = useMemo(() => data?.tracks.slice(0, 10) ?? null, [data]);
  useResolveAll(tracks);
  const title = data?.title ?? CHART_NAMES[src] ?? 'Hitlijst';
  if (error) return <RowError title={title} error={error} />;
  return (
    <section className="music-row" aria-label={title}>
      <RowHead title={title} badge={`${data?.provider ?? ''} · hitlijst`} more={{ label: 'Hele lijst', href: `/music/chart?src=${src}` }} />
      {!data ? <div className="row-skeleton" /> : (
        <ol className="chart-preview" data-focus-group>
          {data.tracks.slice(0, 10).map((t, i) => {
            const via = viaLabel(t);
            return (
              <li key={t.id}>
                <button className="chart-item" onClick={() => playQueue(data.tracks, i)}>
                  <span className="chart-no">{i + 1}</span>
                  <span className="track-art" style={t.artwork ? { backgroundImage: `url("${t.artwork}")` } : undefined} />
                  <span className="track-text"><strong>{t.title}</strong><span className="subtle">{t.artist}</span></span>
                  <span className={`music-src via-${via.tone}`}>{via.text}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function NewAlbumsRow() {
  const { data, error } = useCat<{ albums: CatAlbum[] }>('/api/music/cat/new-albums');
  if (error) return <RowError title="Nieuwe albums" error={error} />;
  return <CardRow title="Nieuwe albums · Deezer" cards={data ? data.albums.map(dzAlbumCard) : null} />;
}

function PlexRecentRow({ plex, musicLibs }: { plex: PlexClient; musicLibs: string[] }) {
  const [cards, setCards] = useState<Card[] | null>(null);
  useEffect(() => {
    Promise.all(musicLibs.map((k) => plex.get<Container<{ Metadata?: PlexItem[] }>>(`/library/sections/${k}/all`, { type: 9, sort: 'addedAt:desc', 'X-Plex-Container-Start': 0, 'X-Plex-Container-Size': 24 }).catch(() => null)))
      .then((rs) => setCards(rs.flatMap((r) => r?.MediaContainer.Metadata ?? []).sort((a, b) => Number((b as { addedAt?: number }).addedAt ?? 0) - Number((a as { addedAt?: number }).addedAt ?? 0)).slice(0, 24).map((a) => plexAlbumCard(plex, a))));
  }, [musicLibs.join()]);
  return <CardRow title="Recent toegevoegd in Plex" cards={cards} more={{ label: 'Alle albums', href: '/music/albums' }} />;
}

function GenresRow() {
  const { data, error } = useCat<{ genres: CatGenre[] }>('/api/music/cat/genres');
  if (error) return <RowError title="Genres" error={error} />;
  return (
    <section className="music-row" aria-label="Genres">
      <RowHead title="Genres" badge="Deezer" more={{ label: 'Ontdekken', href: '/music/discover' }} />
      {!data ? <div className="row-skeleton" /> : <GenreTiles genres={data.genres.slice(0, 12)} />}
    </section>
  );
}

function GenreTiles({ genres }: { genres: CatGenre[] }) {
  return (
    <div className="genre-tiles" data-focus-group>
      {genres.map((g) => (
        <a key={g.id} className="genre-tile" style={g.artwork ? { backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.75)), url("${g.artwork}")` } : undefined} {...linkProps(`/music/genre?id=${g.id}&name=${encodeURIComponent(g.name)}`)}>
          <strong>{g.name}</strong>
        </a>
      ))}
    </div>
  );
}

function RecsRow() {
  const { data, error } = useCat<{ artists: CatArtist[]; linked: boolean }>('/api/music/cat/recs');
  if (error) return <RowError title="Aanbevolen voor jou" error={error} />;
  if (data && !data.linked) {
    return (
      <section className="music-row" aria-label="Aanbevolen voor jou">
        <RowHead title="Aanbevolen voor jou" badge="Last.fm" />
        <p className="subtle">Koppel je Last.fm-account bij <a {...linkProps('/settings/connections')}>Instellingen → Koppelingen</a> voor aanbevelingen op basis van wat je luistert.</p>
      </section>
    );
  }
  return <CardRow title="Aanbevolen voor jou · Last.fm" cards={data ? data.artists.map(dzArtistCard) : null} />;
}

function DzPlaylistsRow() {
  const { data, error } = useCat<{ playlists: CatPlaylist[] }>('/api/music/cat/playlists');
  if (error) return <RowError title="Populaire playlists" error={error} />;
  return <CardRow title="Populaire playlists · Deezer" cards={data ? data.playlists.map(dzPlaylistCard) : null} />;
}

function GenreRow({ id, title }: { id: string; title: string }) {
  const { data, error } = useCat<{ albums: CatAlbum[] }>(`/api/music/cat/genre/${id}`);
  if (error) return <RowError title={title} error={error} />;
  return <CardRow title={`${title} · Deezer`} cards={data ? data.albums.map(dzAlbumCard) : null} more={{ label: 'Genre openen', href: `/music/genre?id=${id}&name=${encodeURIComponent(title)}` }} />;
}

function RowsEditor({ rows: initial, onClose, onSave }: { rows: HomeRow[]; onClose: () => void; onSave: (rows: HomeRow[] | null) => void }) {
  const [rows, setRows] = useState(initial);
  const [adding, setAdding] = useState(false);
  const { data: genres } = useCat<{ genres: CatGenre[] }>(adding ? '/api/music/cat/genres' : null);
  const { data: sources } = useCat<{ lastfm: boolean; charts: { id: string; title: string }[] }>('/api/music/cat/sources');
  const move = (i: number, d: -1 | 1) => setRows((r) => {
    const n = [...r];
    const j = i + d;
    if (j < 0 || j >= n.length) return r;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const add = (r: HomeRow) => {
    setRows((list) => (list.some((x) => x.id === r.id) ? list.map((x) => (x.id === r.id ? { ...x, on: true } : x)) : [...list, r]));
    setAdding(false);
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal rows-editor" role="dialog" aria-modal="true" aria-label="Rijen aanpassen" onClick={(e) => e.stopPropagation()}>
        <div className="rows-head">
          <h2>Rijen op muziek-Home</h2>
          <span className="subtle">Alleen voor jouw profiel</span>
        </div>
        <ol className="rows-list" data-focus-group>
          {rows.map((r, i) => (
            <li key={r.id} className="rows-item">
              <div className="rows-move">
                <button className="mp-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`${r.title} omhoog`}>↑</button>
                <button className="mp-btn" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label={`${r.title} omlaag`}>↓</button>
              </div>
              <span className="rows-title">{r.title ?? r.kind}</span>
              <span className="music-badge">{rowSource(r)}</span>
              {(r.kind === 'genre' || (r.kind === 'chart' && r.id !== 'chart:apple-nl' && r.id !== 'chart:deezer')) && (
                <button className="mp-btn" onClick={() => setRows((l) => l.filter((x) => x.id !== r.id))} aria-label={`${r.title} verwijderen`}>✕</button>
              )}
              <span className="toggle"><input type="checkbox" checked={r.on} onChange={(e) => setRows((l) => l.map((x) => (x.id === r.id ? { ...x, on: e.target.checked } : x)))} aria-label={`${r.title} tonen`} /></span>
            </li>
          ))}
        </ol>
        {adding ? (
          <div className="rows-add">
            <strong>Hitlijst</strong>
            <div className="chip-wrap">
              {(sources?.charts ?? []).map((c) => <button key={c.id} className="chip" onClick={() => add({ id: `chart:${c.id}`, kind: 'chart', on: true, param: c.id, title: c.title })}>{c.title}</button>)}
            </div>
            <strong>Genre (Deezer)</strong>
            <div className="chip-wrap">
              {(genres?.genres ?? []).map((g) => <button key={g.id} className="chip" onClick={() => add({ id: `genre:${g.id}`, kind: 'genre', on: true, param: g.id, title: g.name })}>{g.name}</button>)}
            </div>
            <button className="btn btn-sm" onClick={() => setAdding(false)}>Annuleren</button>
          </div>
        ) : (
          <button className="btn btn-sm rows-add-btn" onClick={() => setAdding(true)}>＋ Rij toevoegen (hitlijst of genre)</button>
        )}
        <p className="subtle hint">
          Lijsten komen uit Deezer en Apple Music (geen sleutel nodig){sources?.lastfm ? ' en je Last.fm-account' : '; koppel Last.fm bij Instellingen → Koppelingen voor aanbevelingen'}.
          Een nummer uit een lijst wordt eerst in je Plex gezocht en daarna in je muziek-addons (volgorde volgens Audio &amp; video).
        </p>
        <div className="form-actions">
          <button className="btn btn-primary" onClick={() => { onSave(rows); onClose(); }}>Klaar</button>
          <button className="btn" onClick={() => { onSave(null); onClose(); }}>Standaard herstellen</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Pagina's ----------

function ChartPage({ src }: { src: string }) {
  const { data: sources } = useCat<{ charts: { id: string; title: string }[] }>('/api/music/cat/sources');
  const { data, error } = useCat<Chart>(`/api/music/cat/chart?src=${src}`);
  const tick = useResolveAll(data?.tracks ?? null);
  const counts = useMemo(() => {
    const c = { plex: 0, addon: 0, none: 0, busy: 0 };
    for (const t of data?.tracks ?? []) c[viaLabel(t).tone]++;
    return c;
  }, [data, tick]);
  const [saved, setSaved] = useState<string | null>(null);
  const saveAsPlaylist = async () => {
    if (!data) return;
    const r = await call<{ playlist: { id: string } }>('/api/music/playlists', { method: 'POST', body: JSON.stringify({ name: `${data.title} (${new Date().toLocaleDateString('nl-NL')})` }) }).catch(() => null);
    if (!r) return;
    await call(`/api/music/playlists/${r.playlist.id}`, { method: 'PATCH', body: JSON.stringify({ add: data.tracks.map(storable) }) }).catch(() => undefined);
    libraryChanged();
    setSaved(r.playlist.id);
  };
  return (
    <>
      <div className="filterbar music-filter" role="tablist" aria-label="Hitlijsten">
        {(sources?.charts ?? [{ id: 'apple-nl', title: 'Top 100 Nederland' }]).map((c) => (
          <a key={c.id} role="tab" className="chip" aria-selected={c.id === src} aria-pressed={c.id === src} {...linkProps(`/music/chart?src=${c.id}`)}>{c.title}</a>
        ))}
      </div>
      {error && <p className="placeholder-row error">{error}</p>}
      {!data && !error && <div className="row-skeleton" />}
      {data && (
        <>
          <header className="music-hero">
            <span className="music-hero-art chart-art">{data.title}</span>
            <div>
              <span className="eyebrow">Hitlijst · {data.provider}</span>
              <h1>{data.title}</h1>
              <p className="subtle">
                {data.tracks.length} nummers · <span className="via-plex-text">{counts.plex + counts.addon} te spelen</span> ({counts.plex} uit Plex, {counts.addon} uit addons{counts.busy ? `, nog ${counts.busy} zoeken` : ''})
              </p>
              <div className="hero-actions" data-focus-group>
                <button className="btn btn-primary" onClick={() => playQueue(data.tracks, 0)}>▶ Afspelen</button>
                <button className="btn" onClick={() => playQueue([...data.tracks].sort(() => Math.random() - 0.5), 0)}>⤮ Shuffle</button>
                {saved ? <a className="btn" {...linkProps(`/music/playlist?id=${saved}`)}>✓ Bewaard · openen</a> : <button className="btn" onClick={() => void saveAsPlaylist()}>＋ Bewaar als playlist</button>}
              </div>
            </div>
          </header>
          <TrackList tracks={data.tracks} numbered showSource />
        </>
      )}
    </>
  );
}

function DiscoverPage() {
  const genres = useCat<{ genres: CatGenre[] }>('/api/music/cat/genres');
  return (
    <>
      <header className="music-hello"><h1>Ontdekken</h1><span className="subtle">Deezer</span></header>
      <NewAlbumsRow />
      <DzPlaylistsRow />
      <section className="music-row" aria-label="Alle genres">
        <RowHead title="Alle genres" />
        {genres.error ? <p className="subtle">{genres.error}</p> : !genres.data ? <div className="row-skeleton" /> : <GenreTiles genres={genres.data.genres} />}
      </section>
    </>
  );
}

function GenrePage({ id, name }: { id: string; name: string }) {
  const { data, error } = useCat<{ name: string; tracks: MusicTrack[]; albums: CatAlbum[]; artists: CatArtist[]; playlists: CatPlaylist[] }>(`/api/music/cat/genre/${id}`);
  if (error) return <p className="placeholder-row error">{error}</p>;
  if (!data) return <div className="row-skeleton" />;
  return (
    <>
      <header className="music-hero">
        <div>
          <span className="eyebrow">Genre · Deezer</span>
          <h1>{data.name || name}</h1>
          <div className="hero-actions" data-focus-group>
            <button className="btn btn-primary" disabled={!data.tracks.length} onClick={() => playQueue(data.tracks, 0)}>▶ Top-nummers afspelen</button>
          </div>
        </div>
      </header>
      {data.tracks.length > 0 && (
        <section className="music-row" aria-label="Top-nummers">
          <RowHead title="Top-nummers" />
          <TrackList tracks={data.tracks.slice(0, 20)} numbered showSource />
        </section>
      )}
      <CardRow title="Albums" cards={data.albums.map(dzAlbumCard)} />
      <CardRow title="Artiesten" cards={data.artists.map(dzArtistCard)} />
      <CardRow title="Playlists" cards={data.playlists.map(dzPlaylistCard)} />
    </>
  );
}

function DzPlaylistPage({ id }: { id: string }) {
  const { data, error } = useCat<{ playlist: CatPlaylist & { description: string | null; tracks: MusicTrack[] } }>(`/api/music/cat/playlist/${encodeURIComponent(id)}`);
  if (error) return <p className="placeholder-row error">{error}</p>;
  if (!data) return <div className="row-skeleton" />;
  return <TracksPage title={data.playlist.title} sub="Playlist · Deezer" art={data.playlist.artwork} tracks={data.playlist.tracks} extra={<PlaylistExtras tracks={data.playlist.tracks} name={data.playlist.title} save={{ kind: 'playlist', src: 'deezer', id, title: data.playlist.title, sub: data.playlist.sub, artwork: data.playlist.artwork }} />} />;
}
